import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ShoppingCart, Server, Loader2, AlertCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { toast } from "sonner";

const DEFAULTS = { plan_price: 69, plan_currency: "€" };

export default function TarifsPage() {
  const [pricing, setPricing] = useState(DEFAULTS);
  const [params] = useSearchParams();
  const [showBuy, setShowBuy] = useState(false);
  const [form, setForm] = useState({ email: "", name: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/public/pricing").then(({ data }) => setPricing({ ...DEFAULTS, ...data })).catch(() => {});
    if (params.get("canceled") === "1") {
      toast.info("Paiement annulé. Vous pouvez réessayer à tout moment.");
    }
  }, [params]);

  const startCheckout = async (e) => {
    e?.preventDefault?.();
    if (!form.email.trim() || !form.email.includes("@")) {
      toast.error("Saisissez votre email pour recevoir la clé de licence.");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/checkout/session", {
        origin_url: origin,
        email: form.email.trim(),
        name: form.name.trim(),
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("URL Stripe manquante");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="tarifs-page">
      <PublicHeader />

      <section className="max-w-6xl mx-auto px-6 pt-16 pb-12 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">// Tarif simple et transparent</p>
        <h1 className="text-5xl font-black tracking-tight mb-6">Un seul prix, tout inclus</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Pas d&apos;abonnement, pas de surprise. Paiement unique, mises à jour à vie incluses.
        </p>
      </section>

      <section className="max-w-2xl mx-auto px-6 pb-16">
        <div className="relative rounded-2xl border-2 border-primary shadow-2xl bg-card p-10" data-testid="plan-card-wilink">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider px-4 py-1 rounded-full">
            Offre unique
          </div>

          <div className="text-center mb-8">
            <div className="h-14 w-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4">
              <Server className="h-7 w-7" />
            </div>
            <h2 className="text-3xl font-black mb-2">WiLink Self-hosted</h2>
            <p className="text-sm text-muted-foreground">Portail interne complet, à installer sur votre serveur</p>
          </div>

          <div className="text-center mb-8">
            <span className="text-7xl font-black">{pricing.plan_price}{pricing.plan_currency}</span>
            <p className="text-sm text-muted-foreground mt-2">Paiement unique · Mises à jour incluses à vie</p>
          </div>

          <ul className="space-y-3 mb-8 max-w-md mx-auto">
            {[
              "Téléchargement immédiat du ZIP PHP/MySQL",
              "Installation sur n'importe quel hébergement (PHP 7.4+)",
              "Mises à jour à vie en 1 clic depuis votre backoffice",
              "Sources complètes incluses (pas de minification)",
              "Whitelist IP, 2FA, sous-catégories, RDP, annonces",
              "Logo & thème personnalisables",
              "Support email",
              "Licence à vie pour 1 domaine",
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <Check className="h-5 w-5 text-primary mt-0.5 shrink-0" /> <span>{f}</span>
              </li>
            ))}
          </ul>

          {!showBuy ? (
            <button
              onClick={() => setShowBuy(true)}
              className="h-14 w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center justify-center gap-2 text-base"
              data-testid="plan-cta-wilink"
            >
              <ShoppingCart className="h-5 w-5" /> Commander WiLink — {pricing.plan_price}{pricing.plan_currency}
            </button>
          ) : (
            <form onSubmit={startCheckout} className="space-y-3" data-testid="checkout-form">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground block mb-1.5">Email (pour recevoir la clé) *</label>
                <input
                  type="email"
                  required
                  autoFocus
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="vous@entreprise.com"
                  className="w-full h-11 px-4 rounded-md border border-border bg-background"
                  data-testid="buyer-email"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-muted-foreground block mb-1.5">Nom (optionnel)</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Votre nom"
                  className="w-full h-11 px-4 rounded-md border border-border bg-background"
                  data-testid="buyer-name"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="h-14 w-full rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center justify-center gap-2 text-base disabled:opacity-60"
                data-testid="proceed-stripe-btn"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Redirection vers Stripe…</>
                ) : (
                  <><ShoppingCart className="h-5 w-5" /> Payer {pricing.plan_price}{pricing.plan_currency} via Stripe</>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowBuy(false)}
                className="text-xs text-muted-foreground hover:text-foreground underline block mx-auto"
              >
                Annuler
              </button>
            </form>
          )}

          <p className="text-xs text-center text-muted-foreground mt-4">
            Paiement sécurisé Stripe · TVA incluse · Téléchargement et clé de licence par email
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-16 border-t border-border">
        <h2 className="text-2xl font-bold mb-8 text-center">Questions fréquentes</h2>
        <div className="space-y-3">
          {[
            { q: "À vie, vraiment ?", a: "Oui. Vous payez 69€ une fois et vous avez accès au logiciel + toutes les futures mises à jour à vie. Pas d'abonnement caché." },
            { q: "Sur combien de sites puis-je l'installer ?", a: "1 licence = 1 domaine. La clé est verrouillée sur votre domaine au premier démarrage. Pour plusieurs sites, achetez plusieurs licences." },
            { q: "Comment fonctionnent les mises à jour ?", a: "Depuis le backoffice de votre installation, un bouton 'Mise à jour du code' télécharge la dernière version depuis nos serveurs. Vos données restent intactes." },
            { q: "Quels prérequis serveur ?", a: "PHP 7.4 ou plus récent + MySQL/MariaDB. Compatible avec OVH, Ionos, o2switch, Hostinger, et tous les hébergements mutualisés standard." },
            { q: "Si je n'ai plus besoin du service, je perds quelque chose ?", a: "Non. Le code reste sur votre serveur, vous gardez vos données. Vous ne recevrez juste plus les mises à jour si votre licence est désactivée." },
            { q: "Remboursement ?", a: "Satisfait ou remboursé sous 14 jours après l'achat." },
          ].map((item, i) => (
            <details key={i} className="border border-border rounded-md bg-card p-4" data-testid={`faq-${i}`}>
              <summary className="font-medium cursor-pointer">{item.q}</summary>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
